import {
  ScenarioPathsState,
  ScenarioPathsStatus,
} from '../hooks/useScenarioPaths';
import React, { forwardRef, useCallback, useContext } from 'react';
import { ProgressBar } from '../helpers/ProgressBar';
import { PathFinderControls } from '../pathBuilder';
import { PartialRecord, PathReport, Scenario } from '@/types';
import { PathReportSidebar } from '../modes/PathReportSidebar';
import {
  PathBuilderControls,
  PathBuilderControlsRef,
} from '../pathBuilder/PathBuilderControls';
import { BelowMapContext } from '../layouts/BelowMapContext';

export enum ScenarioPageMode {
  NONE = 'NONE',
  PATH_FINDER = 'PATH_FINDER',
  PATH_REPORT = 'PATH_REPORT',
  PATH_BUILDER = 'PATH_BUILDER',
}

const TAB_MODES: PartialRecord<string, ScenarioPageMode> = {
  'path-finder': ScenarioPageMode.PATH_FINDER,
  'path-report': ScenarioPageMode.PATH_REPORT,
  'path-builder': ScenarioPageMode.PATH_BUILDER,
};

export type ScenarioPathStateDetailProps = {
  scenario: Scenario;
  state: ScenarioPathsState;
  onBuild: () => void;
  onGetPaths?: (paths: PathReport[]) => void;
};

export const ScenarioPathStateDetail = forwardRef<
  PathBuilderControlsRef,
  ScenarioPathStateDetailProps
>(({ scenario, state, onBuild, onGetPaths }, ref) => {
  const belowMap = useContext(BelowMapContext);
  const onRefresh = useCallback(() => {
    if (
      confirm(
        'Are you sure you want to refresh the path data? This will reset the current state and may take a few minutes.',
      )
    ) {
      onBuild();
    }
  }, [onBuild]);

  const activeTab = belowMap?.activeTab ?? '';
  const mode = TAB_MODES[activeTab] ?? ScenarioPageMode.NONE;

  return (
    <div className="d-flex flex-column mt-2">
      {[
        ScenarioPathsStatus.IDLE,
        ScenarioPathsStatus.INITIALIZING,
        ScenarioPathsStatus.CANCELLED,
      ].includes(state.status) && (
        <button
          className="btn btn-primary form-control justify-content-center align-items-center"
          disabled={state.status === ScenarioPathsStatus.INITIALIZING}
          onClick={onBuild}
        >
          {state.status === ScenarioPathsStatus.INITIALIZING ? (
            <>
              Checking Paths State
              <span
                className="spinner-border ms-1"
                style={{
                  color: 'white',
                  width: '1rem',
                  height: '1rem',
                  borderWidth: '0.15em',
                }}
                role="status"
              />
            </>
          ) : (
            'Build Paths State'
          )}
        </button>
      )}
      {state.status === ScenarioPathsStatus.RUNNING && (
        <ProgressBar percent={state.progress.percent} />
      )}
      {state.status === ScenarioPathsStatus.CANCELLED ? (
        <p className="text-warning">
          {state.message || 'The path service build was cancelled'}
        </p>
      ) : state.status === ScenarioPathsStatus.ERROR ? (
        <>
          <p className="text-danger">
            {state.socketError ||
              state.serviceError ||
              state.message ||
              'An unknown error occurred'}
          </p>
          <div className="d-block mt-2 mb-3">
            <span className="text-primary" role="button" onClick={onRefresh}>
              Rebuild Path Service
            </span>
          </div>
        </>
      ) : (
        <p className="text-muted">{state.progress.step}</p>
      )}
      {state.status === ScenarioPathsStatus.SUCCESS && (
        <>
          {mode === ScenarioPageMode.PATH_FINDER && <h2>Path Finder</h2>}
          {mode === ScenarioPageMode.PATH_REPORT && <h2>Path Report</h2>}
          {mode === ScenarioPageMode.PATH_BUILDER && <h2>Path Builder</h2>}
          <div className="d-block mt-2 mb-3">
            <span className="text-primary" role="button" onClick={onRefresh}>
              Refresh Path Service
            </span>
          </div>
          <div
            className={mode !== ScenarioPageMode.PATH_FINDER ? 'd-none' : ''}
          >
            <PathFinderControls
              scenario_id={scenario.id}
              gas_date={scenario.flow_date}
              progress={state.progress}
            />
          </div>
          <div
            className={mode !== ScenarioPageMode.PATH_REPORT ? 'd-none' : ''}
          >
            <PathReportSidebar />
          </div>
          <div
            className={mode !== ScenarioPageMode.PATH_BUILDER ? 'd-none' : ''}
          >
            <PathBuilderControls
              ref={ref}
              scenarioId={scenario.id}
              gasDate={scenario.flow_date}
              onFulfill={onGetPaths}
            />
          </div>
        </>
      )}
    </div>
  );
});
